/*
 * Shared Pixmoto video mapping helper.
 *
 * This exposes:
 *   window.PixmotoVideoMapping.createRunner(options)
 */
import { injectPlayShellPreloadHints } from "@/modules/experiencePrefetchWarm";
import type { ExperiencePublishData } from "@/types/experience";
import { buildAnchor } from "@/mapping/anchor";
import { prefetchExperienceData } from "@/mapping/experiencePrefetch";
import {
  buildAnchorFill,
  ensureAspectRatio,
  forceShowPixmotoAnchor,
  hideLegacyPlayerUi,
  setTargetsPending,
  whenTargetsReady,
} from "@/mapping/targetLayout";

export interface MappingRunnerOptions {
  /** Pass when run() is deferred: document.currentScript is gone by then. */
  scriptElement?: HTMLOrSVGScriptElement | null;
  /** Reads `<prefix>_PIXMOTO_*` window overrides. */
  runtimePrefix?: string;
  /** One selector, or several tried in order (the first with matches wins). */
  targetSelector?: string | string[];
  targetIdSelectors?: string[];
  /** Page-wide selectors removed once the mapping succeeds. */
  removeSelectors?: string[];
}

type PublishData = ExperiencePublishData | null | undefined;

(() => {
  // Production defaults. Local/dev overrides via `data-*` on the mapping
  // script tag(s) — see README "Video mapping overrides".
  const DEFAULT_API_BASE = "admin-api.pixmoto.com";
  const DEFAULT_API_PROTOCOL = "https";
  const DEFAULT_POPOVER_SCRIPT_SRC = "//play.pixmoto.com/js/popover.js";
  const DEFAULT_ASSET_SERVER = "assets.pixmoto.com";

  const MAPPING_COMMON_SCRIPT_SELECTOR =
    'script[src*="pixmoto_video_mapping_common.js"]';

  const normalizeOverrideValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return "";
    }
    const normalized = String(value).trim();
    if (!normalized) {
      return "";
    }
    const lowered = normalized.toLowerCase();
    if (lowered === "null" || lowered === "undefined") {
      return "";
    }
    return normalized;
  };

  function createRunner(options?: MappingRunnerOptions): () => void {
    const opts: MappingRunnerOptions = options || {};
    // Callers should pass scriptElement when run() is deferred (e.g. DOMContentLoaded),
    // because document.currentScript is only set during synchronous script evaluation.
    const scriptEl = opts.scriptElement || document.currentScript || null;
    const runtimePrefix = opts.runtimePrefix || "";
    // `targetSelector` may be a single CSS selector string, or an array of
    // selectors checked in priority order — the first selector that yields
    // at least one match wins, the rest are skipped. This lets callers
    // declare a primary target (e.g. video embed wrapper) with a fallback
    // for image blocks when no video embed is present on the page.
    const targetSelectors = (() => {
      const raw = opts.targetSelector;
      if (Array.isArray(raw)) {
        return raw.filter(s => typeof s === "string" && s.trim());
      }
      if (typeof raw === "string" && raw.trim()) {
        return [raw];
      }
      return [".sqs-video-overlay"];
    })();
    const targetIdSelectors = Array.isArray(opts.targetIdSelectors)
      ? opts.targetIdSelectors.filter(id => typeof id === "string" && id)
      : [];
    // Optional CSS selectors for elements to delete once the mapping
    // succeeds (e.g. a duplicate "watch the video" CTA that lives outside
    // the mapped target). Removal is PAGE-WIDE by design — selectors run
    // against the whole `document`, not scoped to the mapped target/wrapper
    // — because these elements typically sit in a sibling block. Callers
    // must pass selectors specific enough not to match unrelated elements.
    // Applied only on the success path; each selector is try/catch-guarded.
    const removeSelectors = Array.isArray(opts.removeSelectors)
      ? opts.removeSelectors.filter(s => typeof s === "string" && s.trim())
      : [];

    function findTargets(): ArrayLike<HTMLElement> {
      for (let i = 0; i < targetSelectors.length; i++) {
        const matches = document.querySelectorAll<HTMLElement>(
          targetSelectors[i],
        );
        if (matches && matches.length > 0) {
          return matches;
        }
      }

      for (let i = 0; i < targetIdSelectors.length; i++) {
        const el = document.getElementById(targetIdSelectors[i]);
        if (el) {
          return [el];
        }
      }
      return [];
    }

    const getRuntimeOverride = (name: string): string => {
      if (scriptEl) {
        const fromRunner = normalizeOverrideValue(scriptEl.getAttribute(name));
        if (fromRunner) {
          return fromRunner;
        }
      }
      const commonScript = document.querySelector(
        MAPPING_COMMON_SCRIPT_SELECTOR,
      );
      if (commonScript) {
        return normalizeOverrideValue(commonScript.getAttribute(name));
      }
      return "";
    };

    const getWindowOverride = (suffix: `PIXMOTO_${string}`): string => {
      if (!runtimePrefix) {
        return "";
      }
      return normalizeOverrideValue(
        window[`${runtimePrefix}_${suffix}` as const],
      );
    };

    function getApiBaseUrl(): string {
      return (
        window.PIXMOTO_API_BASE_URL ||
        getWindowOverride("PIXMOTO_API_BASE_URL") ||
        getRuntimeOverride("data-api-base-url") ||
        DEFAULT_API_BASE
      );
    }

    function getApiProtocol(): string {
      return (
        window.PIXMOTO_API_PROTOCOL ||
        getWindowOverride("PIXMOTO_API_PROTOCOL") ||
        getRuntimeOverride("data-api-protocol") ||
        DEFAULT_API_PROTOCOL
      );
    }

    function getPlayerUrlOverride(): string {
      return normalizeOverrideValue(
        getWindowOverride("PIXMOTO_PLAYER_URL") ||
          getRuntimeOverride("data-player-url") ||
          "",
      );
    }

    function getTargetWindowOverride(): string {
      return normalizeOverrideValue(
        getWindowOverride("PIXMOTO_TARGET_WINDOW") ||
          getRuntimeOverride("data-target-window") ||
          "",
      );
    }

    function getPopoverScriptSrc(): string {
      return (
        window.PIXMOTO_POPOVER_SCRIPT_SRC ||
        getWindowOverride("PIXMOTO_POPOVER_SCRIPT_SRC") ||
        getRuntimeOverride("data-popover-script-src") ||
        DEFAULT_POPOVER_SCRIPT_SRC
      );
    }

    function getAssetServerHost(): string {
      return (
        window.PIXMOTO_ASSET_SERVER ||
        getWindowOverride("PIXMOTO_ASSET_SERVER") ||
        getRuntimeOverride("data-asset-server") ||
        DEFAULT_ASSET_SERVER
      );
    }

    function getPublishDataApiUrl(pageUrl: string): string {
      return `${getApiProtocol()}://${getApiBaseUrl()}/publisher/experience-publish-data?url=${encodeURIComponent(pageUrl)}`;
    }

    function getExperienceByUrlApiUrl(pageUrl: string): string {
      return `${getApiProtocol()}://${getApiBaseUrl()}/publisher/experience?url=${encodeURIComponent(pageUrl)}`;
    }

    function getPlayServerHost(): string {
      const popoverSrc = getPopoverScriptSrc();
      try {
        const u = new URL(popoverSrc, window.location.href);
        return u.host;
      } catch (_e) {
        return "play.pixmoto.com";
      }
    }

    function normalizePosterSrc(poster: string | undefined): string {
      if (!poster) {
        return "";
      }
      if (/^https?:\/\//i.test(poster) || /^\/\//.test(poster)) {
        return poster;
      }
      const cleaned = String(poster).replace(/^\/+/, "");
      return `//${getAssetServerHost()}/${cleaned}`;
    }

    function ensurePopoverScript() {
      const src = getPopoverScriptSrc();
      const existing = document.querySelector('script[src*="/js/popover.js"]');
      if (existing) {
        return;
      }

      const s = document.createElement("script");
      s.src = src;
      document.body.appendChild(s);
    }

    function activatePopover() {
      const hostPopover = window.__pixmoto?.popover;
      if (typeof hostPopover?.reload === "function") {
        hostPopover.reload();
        return;
      }
      const playerPopover = window.pixmoto?.popover;
      if (typeof playerPopover?.reload === "function") {
        playerPopover.reload();
      }
    }

    function removeConfiguredElements() {
      for (let i = 0; i < removeSelectors.length; i++) {
        try {
          const els = document.querySelectorAll(removeSelectors[i]);
          for (let j = 0; j < els.length; j++) {
            els[j].remove();
          }
        } catch (_e) {
          // Invalid/unsupported selector (e.g. `:has` on an old engine) —
          // skip it; never let element removal break the mapping.
        }
      }
    }

    function replaceTargetChildren(
      pageUrl: string,
      posterSrc: string,
      publishData: PublishData,
    ) {
      const targets = findTargets();
      if (!targets || targets.length === 0) {
        return;
      }

      for (let i = 0; i < targets.length; i++) {
        const target = targets[i];
        ensureAspectRatio(target);
        hideLegacyPlayerUi(target);
        while (target.firstChild) {
          target.removeChild(target.firstChild);
        }
        const anchor = buildAnchor(
          { getPlayServerHost, getPlayerUrlOverride, getTargetWindowOverride },
          pageUrl,
          posterSrc,
          publishData,
        );
        const fill = buildAnchorFill();
        fill.appendChild(anchor);
        target.appendChild(fill);
        forceShowPixmotoAnchor(anchor);
      }
    }

    return function run() {
      const targets = findTargets();
      if (!targets || targets.length === 0) {
        return;
      }
      setTargetsPending(targets, true);

      const pageUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      const apiUrl = getPublishDataApiUrl(pageUrl);

      fetch(apiUrl, { method: "GET" })
        .then(res => {
          if (!res.ok) {
            throw new Error(
              `Publish-data API failed with status ${res.status}`,
            );
          }
          return res.json();
        })
        .then((publishData: PublishData) => {
          const posterSrc = normalizePosterSrc(publishData?.poster);
          if (!posterSrc) {
            setTargetsPending(targets, false);
            return;
          }
          // Publish data resolved → mapping is viable. Kick off the
          // publisher experience prefetch + play shell preloads
          // in parallel with layout work so by the time the user
          // clicks play, the iframe can read the API payload
          // from `window.__pixmoto.prefetchedExperiences` and the
          // play bundle is in cache. Mirrors the prefetch path
          // `popover.js` runs for explicit anchors (with expId),
          // but keyed by URL since mapping anchors don't carry an
          // experience id at insertion time.
          injectPlayShellPreloadHints(getPlayServerHost());
          prefetchExperienceData(pageUrl, getExperienceByUrlApiUrl);
          // Wait for the original media inside each target to settle
          // (so the wrapper has its final layout-settled height) and
          // then defer one frame so any sync layout work that runs
          // in `load` listeners (Squarespace's image-block JS) has
          // committed before we measure in `ensureAspectRatio`.
          whenTargetsReady(targets, () => {
            const apply = () => {
              replaceTargetChildren(pageUrl, posterSrc, publishData);
              removeConfiguredElements();
              ensurePopoverScript();
              activatePopover();
              setTargetsPending(targets, false);
            };
            if (typeof window.requestAnimationFrame === "function") {
              window.requestAnimationFrame(apply);
            } else {
              apply();
            }
          });
        })
        .catch(() => {
          // Silent fail by design for safe drop-in usage.
          setTargetsPending(targets, false);
        });
    };
  }

  if (window.PixmotoVideoMapping) {
    window.PixmotoVideoMapping.createRunner = createRunner;
  } else {
    window.PixmotoVideoMapping = { createRunner };
  }
})();
